export class JustRemoveWidget extends HTMLElement {
    private socialApi = 'https://api-social.kaywat.codes';
    private brokerApi = 'https://fulfillment.kaywat.codes';
    
    private mode: string | null = null;
    private query: string | null = null;
    private email: string | null = null;
    private source: string | null = null;
    
    private container!: HTMLDivElement;
    private eventSource: EventSource | null = null;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.renderBase();
    }

    static get observedAttributes() {
        return ['mode', 'query', 'email', 'source'];
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (newValue) {
            if (name === 'mode') this.mode = newValue;
            if (name === 'query') this.query = newValue.replace('@', ''); // strip @ for searches
            if (name === 'email') this.email = newValue;
            if (name === 'source') this.source = newValue;
        }
        
        // Wait until mode and query are set
        if (this.mode && (this.query || this.mode === 'request-removal')) {
            this.executeAction();
        }
    }
    
    disconnectedCallback() {
        if (this.eventSource) {
            this.eventSource.close();
        }
    }

    renderBase() {
        this.shadowRoot!.innerHTML = `
            <style>
                :host {
                    display: block;
                    width: 100%;
                    max-width: 600px;
                    margin: 15px 0;
                    font-family: var(--font-family, system-ui, -apple-system, sans-serif);
                }
                .widget-card {
                    background: #111116;
                    border: 1px solid rgba(168, 85, 247, 0.3);
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 8px 30px rgba(0,0,0,0.4);
                }
                .header {
                    background: #181820;
                    padding: 12px 16px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
                }
                .logo {
                    width: 24px;
                    height: 24px;
                    background: #a855f7;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    font-size: 12px;
                    font-weight: bold;
                }
                .title {
                    color: #fff;
                    font-weight: 600;
                    font-size: 14px;
                    letter-spacing: 0.5px;
                }
                .body {
                    padding: 20px;
                }
                .status-box {
                    text-align: center;
                    margin-bottom: 20px;
                }
                .status-text {
                    color: #a855f7;
                    font-size: 14px;
                    font-weight: 500;
                    margin-bottom: 10px;
                }
                .progress-bar {
                    width: 100%;
                    height: 6px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 3px;
                    overflow: hidden;
                }
                .progress-fill {
                    width: 0%;
                    height: 100%;
                    background: linear-gradient(90deg, #a855f7, #c084fc);
                    transition: width 0.3s ease;
                }
                .results-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 12px;
                    max-height: 350px;
                    overflow-y: auto;
                    padding-right: 5px;
                }
                .results-grid::-webkit-scrollbar {
                    width: 6px;
                }
                .results-grid::-webkit-scrollbar-thumb {
                    background: rgba(255,255,255,0.2);
                    border-radius: 3px;
                }
                .result-card {
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 8px;
                    padding: 12px;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }
                .result-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                }
                .result-title {
                    color: #fff;
                    font-size: 15px;
                    font-weight: 600;
                    margin: 0;
                }
                .result-source {
                    background: rgba(168, 85, 247, 0.2);
                    color: #d8b4fe;
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 11px;
                    font-weight: 600;
                }
                .result-desc {
                    color: #a1a1aa;
                    font-size: 13px;
                    margin: 0;
                    line-height: 1.4;
                }
                .removal-btn {
                    margin-top: 6px;
                    background: rgba(239, 68, 68, 0.1);
                    border: 1px solid rgba(239, 68, 68, 0.5);
                    color: #f87171;
                    padding: 6px 12px;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 600;
                    cursor: pointer;
                    text-align: center;
                    transition: all 0.2s;
                }
                .removal-btn:hover {
                    background: rgba(239, 68, 68, 0.2);
                }
                .success-box {
                    background: rgba(16, 185, 129, 0.1);
                    border: 1px solid rgba(16, 185, 129, 0.3);
                    padding: 20px;
                    border-radius: 8px;
                    text-align: center;
                    color: #34d399;
                }
                .error-box {
                    background: rgba(239, 68, 68, 0.1);
                    border: 1px solid rgba(239, 68, 68, 0.3);
                    padding: 20px;
                    border-radius: 8px;
                    text-align: center;
                    color: #f87171;
                }
            </style>
            <div class="widget-card">
                <div class="header">
                    <div class="logo">JR</div>
                    <div class="title" id="widgetTitle">JustRemove.me Engine</div>
                </div>
                <div class="body" id="widgetBody">
                    <div class="status-box" id="statusBox">
                        <div class="status-text" id="statusText">Initializing...</div>
                        <div class="progress-bar"><div class="progress-fill" id="progressFill"></div></div>
                    </div>
                    <div class="results-grid" id="resultsGrid"></div>
                </div>
            </div>
        `;
        this.container = this.shadowRoot!.getElementById('widgetBody') as HTMLDivElement;
    }

    executeAction() {
        if (this.mode === 'social-scan' || this.mode === 'broker-scan') {
            this.runStreamScan();
        } else if (this.mode === 'request-removal') {
            this.runRemovalRequest();
        }
    }

    runStreamScan() {
        const title = this.shadowRoot!.getElementById('widgetTitle')!;
        const statusText = this.shadowRoot!.getElementById('statusText')!;
        const progressFill = this.shadowRoot!.getElementById('progressFill')!;
        const resultsGrid = this.shadowRoot!.getElementById('resultsGrid')!;

        title.textContent = this.mode === 'social-scan' ? 'Social Footprint Scan' : 'Data Broker Scan';
        statusText.textContent = `Scanning web for ${this.query}...`;
        progressFill.style.width = '5%';
        resultsGrid.innerHTML = '';
        
        let url = '';
        if (this.mode === 'social-scan') {
            url = `${this.socialApi}/scan/social?name=${encodeURIComponent(this.query!)}`;
        } else {
            url = `${this.brokerApi}/scan/stream?name=${encodeURIComponent(this.query!)}`;
            if (this.email) {
                url += `&email=${encodeURIComponent(this.email)}`;
            }
        }

        if (this.eventSource) {
            this.eventSource.close();
        }

        this.eventSource = new EventSource(url);

        this.eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                
                if (data.status) {
                    statusText.textContent = data.status;
                }
                if (data.percent !== undefined) {
                    progressFill.style.width = `${data.percent}%`;
                }

                if (data.type === 'finding') {
                    const card = document.createElement('div');
                    card.className = 'result-card';
                    card.innerHTML = `
                        <div class="result-header">
                            <h3 class="result-title">${data.title || data.source}</h3>
                            <span class="result-source">${data.source || 'Web'}</span>
                        </div>
                        <p class="result-desc">${data.detail || 'Data match detected.'}</p>
                    `;
                    resultsGrid.appendChild(card);
                }

                if (data.complete) {
                    this.eventSource!.close();
                    progressFill.style.width = '100%';
                    statusText.textContent = "Scan Complete.";
                    if (resultsGrid.children.length === 0) {
                        resultsGrid.innerHTML = '<div style="color: #a1a1aa; text-align: center; padding: 20px;">No footprint matches found.</div>';
                    }
                }
            } catch (err) {
                console.error("Error parsing SSE data", err);
            }
        };

        this.eventSource.onerror = (err) => {
            console.error("SSE Error:", err);
            this.eventSource!.close();
            statusText.textContent = "Scan finished or interrupted.";
            progressFill.style.width = '100%';
        };
    }

    async runRemovalRequest() {
        const title = this.shadowRoot!.getElementById('widgetTitle')!;
        const statusBox = this.shadowRoot!.getElementById('statusBox')!;
        const resultsGrid = this.shadowRoot!.getElementById('resultsGrid')!;

        title.textContent = 'Automated Opt-Out Request';
        statusBox.style.display = 'none';
        
        if (!this.source || !this.email || !this.query) {
            resultsGrid.innerHTML = `<div class="error-box">Missing required fields (source, email, or query).</div>`;
            return;
        }

        resultsGrid.innerHTML = `<div class="status-text">Dispatching removal request to ${this.source}...</div>`;

        try {
            const res = await fetch(`${this.brokerApi}/request-removal`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    source: this.source,
                    user_email: this.email,
                    user_name: this.query
                })
            });
            
            const data = await res.json();
            
            if (data.sent) {
                resultsGrid.innerHTML = `
                    <div class="success-box">
                        <h3 style="margin:0 0 10px 0;">Request Dispatched</h3>
                        <p style="margin:0; font-size:13px;">Successfully sent legal opt-out request to <b>${this.source}</b> on behalf of <b>${this.query}</b>. Confirmation will be sent to <b>${this.email}</b>.</p>
                    </div>
                `;
            } else {
                resultsGrid.innerHTML = `
                    <div class="error-box">
                        <h3 style="margin:0 0 10px 0;">Notice</h3>
                        <p style="margin:0; font-size:13px;">${data.reason || 'Could not automate request for this broker.'}</p>
                    </div>
                `;
            }
        } catch (err: any) {
            resultsGrid.innerHTML = `<div class="error-box">Error dispatching request: ${err.message}</div>`;
        }
    }
}

export function initJustRemoveBridge() {
    if (!customElements.get('justremove-widget')) {
        customElements.define('justremove-widget', JustRemoveWidget);
    }
}
